import {
  Box,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useRef, useEffect } from "react";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { styled as MuiStyled } from "@mui/material/styles";
import { useDrag, useDrop } from "react-dnd";
import { useFormikContext } from "formik";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IyugaTooltip from "../../../../../IyugaTooltip";
import { ASSETS } from "../../../../../../utils/config";

const VALID_EXTENSIONS = [
  ".com",
  ".org",
  ".net",
  ".edu",
  ".gov",
  ".mil",
  ".io",
  ".co",
  ".ai",
  ".app",
  ".dev",
  ".me",
  ".info",
  ".biz",
  ".uk",
  ".us",
  ".ca",
  ".au",
];

const DragIcon = MuiStyled(DragIndicatorIcon)`
  font-weight: 600;
  font-size: 25px;
  cursor: grab;  
  color: #bbbbbb;
`;

const SocialLinksCard = ({ link, index, handleRemoveLink, name }) => {
  const dragRef = useRef(null);
  const cardRef = useRef(null);

  const { setFieldValue, errors, touched, setFieldTouched, values } =
    useFormikContext();

  const [{ isDragging }, drag, preview] = useDrag({
    type: "CARD",
    item: () => ({ index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const moveCard = (dragIndex, hoverIndex) => {
    const draggedItem = values[name][dragIndex];
    const newLinks = [...values[name]];

    // Remove the dragged item
    newLinks.splice(dragIndex, 1);
    // Insert it at the new position
    newLinks.splice(hoverIndex, 0, draggedItem);

    // Update the form values with the new array order
    setFieldValue(name, newLinks);
  };

  const [{ handlerId }, drop] = useDrop({
    accept: "CARD",
    collect: (monitor) => ({
      handlerId: monitor.getHandlerId(),
    }),
    hover(item, monitor) {
      if (!cardRef.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      //   const hoverBoundingRect = cardRef.current.getBoundingClientRect();
      //   const hoverMiddleY =
      //     (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      //   const clientOffset = monitor.getClientOffset();
      //   const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      //   if (
      //     (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) ||
      //     (dragIndex > hoverIndex && hoverClientY > hoverMiddleY)
      //   ) {
      //     return;
      //   }

      // Perform the move as soon as we hover over a different card
      moveCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  // Only apply drop to the entire card container
  drop(cardRef);

  // Make the icon the drag handle
  drag(dragRef);

  // This ensures the entire card is shown during drag, even if grabbed from the icon
  preview(cardRef);

  const handleTitleChange = (value) => {
    setFieldTouched(`${name}.${index}.title`, true, false);
    setFieldValue(`${name}.${index}.title`, value);
  };

  // Helper function to trim spaces after https:// until first word
  const trimUrlSpaces = (url) => {
    if (!url) return url;

    // Remove spaces after https:// until the first non-space character
    const cleaned = url.replace(/^(https?:\/\/)(\s+)/, "$1");

    // Also trim any leading/trailing spaces from the entire URL
    return cleaned.trim();
  };

  // Remove duplicate https:// from the link value
  useEffect(() => {
    if (link.link) {
      // Remove all duplicate https:// and keep only one at the beginning
      const cleanedLink = link.link.replace(/(https:\/\/)+/g, "https://");

      if (cleanedLink !== link.link) {
        setFieldValue(`${name}.${index}.link`, cleanedLink);

        // Re-fetch favicon for the cleaned URL
        if (!cleanedLink.trim()) {
          setFieldValue(`${name}.${index}.icon`, "");
          return;
        }

        try {
          const domain = new URL(cleanedLink).hostname;

          // Check if domain has any valid extension
          const hasValidExtension = VALID_EXTENSIONS.some((ext) =>
            domain.toLowerCase().endsWith(ext)
          );

          if (!hasValidExtension) {
            setFieldValue(`${name}.${index}.icon`, "");
            return;
          }

          const faviconURL = `https://external-content.duckduckgo.com/ip3/${domain}.ico`;
          setFieldValue(`${name}.${index}.icon`, faviconURL);
        } catch (error) {
          console.error("Invalid URL:", error);
          setFieldValue(`${name}.${index}.icon`, ASSETS.website);
        }
      }
    }
  }, [link.link, setFieldValue, name, index]);

  const handleLinkChange = (value) => {
    setFieldTouched(`${name}.${index}.link`, true, false);

    const transformedValue = value.startsWith("https://")
      ? value
      : !value.startsWith("https:/")
      ? value.replace(/^(https?:\/\/)?/, "https://")
      : "https://";

    setFieldValue(`${name}.${index}.link`, transformedValue);

    if (!value.trim()) {
      setFieldValue(`${name}.${index}.icon`, "");
      return;
    }

    // try {
    //   const domain = new URL(value).hostname;
    //   const faviconURL = `https://www.google.com/s2/favicons?sz=64&domain_url=${domain}`;
    //   setFieldValue(`external_links.${index}.icon`, faviconURL);
    // } catch (error) {
    //   console.error("Invalid URL. Could not fetch favicon.", error);
    //   setFieldValue(`external_links.${index}.icon`, "");
    // }

    try {
      const domain = new URL(value).hostname;

      // Check if domain has any valid extension
      const hasValidExtension = VALID_EXTENSIONS.some((ext) =>
        domain.toLowerCase().endsWith(ext)
      );

      if (!hasValidExtension) {
        setFieldValue(`${name}.${index}.icon`, "");
        return;
      }

      // Choose favicon service based on domain
      // const faviconURL = domain.includes("iyuga")
      //   ? `https://external-content.duckduckgo.com/ip3/${domain}.ico`
      //   : `https://api.faviconkit.com/${domain}/144`;
      //https://logo.clearbit.com/

      const faviconURL = `https://external-content.duckduckgo.com/ip3/${domain}.ico`;

      setFieldValue(`${name}.${index}.icon`, faviconURL);
    } catch (error) {
      console.error("Invalid URL:", error);
      setFieldValue(`${name}.${index}.icon`, ASSETS.website);
    }
  };

  const handleLinkBlur = () => {
    setFieldTouched(`${name}.${index}.link`, true);

    // Trim spaces from the current link value on blur
    const currentValue = link.link;
    const trimmedValue = trimUrlSpaces(currentValue);

    // Only update if trimming actually changed something
    if (trimmedValue !== currentValue) {
      setFieldValue(`${name}.${index}.link`, trimmedValue);

      // Re-fetch favicon for the trimmed URL
      if (!trimmedValue.trim()) {
        setFieldValue(`${name}.${index}.icon`, "");
        return;
      }

      try {
        const domain = new URL(trimmedValue).hostname;

        // Check if domain has any valid extension
        const hasValidExtension = VALID_EXTENSIONS.some((ext) =>
          domain.toLowerCase().endsWith(ext)
        );

        if (!hasValidExtension) {
          setFieldValue(`${name}.${index}.icon`, "");
          return;
        }

        const faviconURL = `https://external-content.duckduckgo.com/ip3/${domain}.ico`;
        setFieldValue(`${name}.${index}.icon`, faviconURL);
      } catch (error) {
        console.error("Invalid URL:", error);
        setFieldValue(`${name}.${index}.icon`, ASSETS.website);
      }
    }
  };

  const hasError = (fieldName) => {
    return (
      touched?.[name]?.[index]?.[fieldName] &&
      errors?.[name]?.[index]?.[fieldName]
    );
  };

  return (
    <Box
      ref={cardRef}
      data-handler-id={handlerId}
      name={`link-${index}`}
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        border: "1px solid #BBBBBB",
        borderRadius: "8px",
        p: "15px 10px",
        opacity: isDragging ? 0.5 : 1,
        gap: "15px",
      }}
    >
      <div ref={dragRef}>
        <DragIcon />
      </div>

      {name !== "business_link" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            width: "30%",
            position: "relative",
          }}
        >
          <TextField
            value={link.title}
            inputProps={{ maxLength: 16 }}
            fullWidth
            placeholder="Add Title"
            error={hasError("title")}
            helperText={hasError("title") && errors?.[name]?.[index]?.title}
            onBlur={() => setFieldTouched(`${name}.${index}.title`, true)}
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "8px",
                height: "44px",
                border:
                  touched?.[name]?.[index]?.title &&
                  errors?.[name]?.[index]?.title
                    ? "1px solid #ED4337"
                    : "1px solid #CED4DA",
                color: "#212529",
                fontSize: "16px",
                fontFamily: "DM Sans",
                fontWeight: "400",
              },
              "& fieldset": {
                border: "none",
              },
              "& .MuiFormHelperText-root": {
                position: "absolute",
                bottom: "-18px",
                color: "#ED4337",
                marginLeft: 0,
                fontFamily: "DM Sans",
              },
            }}
            onChange={(e) => handleTitleChange(e.target.value)}
          />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          width: name === "business_link" ? "80%" : "50%",
          position: "relative",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", position: "relative" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "0 12px",
              height: "44px",
              borderRadius: "8px 0 0 8px",
              border: hasError("link")
                ? "1px solid #ED4337"
                : "1px solid #CED4DA",
              borderRight: "none",
            }}
          >
            <img
              src={link.icon || ASSETS.website}
              alt="link icon"
              style={{
                width: "24px",
                height: "24px",
                objectFit: "contain",
              }}
            />
          </Box>

          <TextField
            value={link.link}
            fullWidth
            placeholder="URL"
            error={hasError("link")}
            helperText={hasError("link") && errors?.[name]?.[index]?.link}
            onBlur={handleLinkBlur}
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "0 8px 8px 0",
                height: "44px",
                border:
                  touched?.[name]?.[index]?.link &&
                  errors?.[name]?.[index]?.link
                    ? "1px solid #ED4337"
                    : "1px solid #CED4DA",
                color: "#212529",
                fontSize: "16px",
                fontFamily: "DM Sans",
                fontWeight: "400",
              },
              "& fieldset": {
                border: "none",
              },
              "& .MuiFormHelperText-root": {
                position: "absolute",
                bottom: "-18px",
                left: "-48px", // This will align with the icon container
                color: "#ED4337",
                marginLeft: 0,
                fontFamily: "DM Sans",
              },
            }}
            onChange={(e) => handleLinkChange(e.target.value)}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "5px",
        }}
      >
        <Box
          onClick={handleRemoveLink}
          sx={{
            background: "#F6F7F9",
            borderRadius: "8px",
            border: "1px solid #E1E1E1",
            cursor: "pointer",
            width: "40px",
            height: "40px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <DeleteOutlineIcon sx={{ color: "#9B5AF8" }} />
        </Box>

        <IyugaTooltip tooltip="Display this link" placement="bottom">
          <Switch
            checked={link.is_cta_enabled}
            onChange={(e) =>
              setFieldValue(`${name}.${index}.is_cta_enabled`, e.target.checked)
            }
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#9B5AF8",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#9B5AF8",
              },
            }}
          />
        </IyugaTooltip>
      </Box>
    </Box>
  );
};

export default SocialLinksCard;
